import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { validateAnswer, validateAnswerOptimistic } from '../../utils/answerValidator';
import { validateInteractiveAnswer, generateCodingTask, generateMCQ, validateGeneratedTask, getGeneratedTasks } from '../../services/api';
import { formatInstructions, formatExpectedOutput } from '../../utils/contentFormatter';
import CodeEditor from '../CodeEditor/CodeEditor';
import McqScreen from './McqScreen';
import './OpenQuestionScreen.css';

const OpenQuestionScreen = ({ screen, onAnswer, userResponse, lessonId, subjectArea }) => {
  const { handleRequest } = useApi();
  const [answer, setAnswer] = useState(userResponse || '');
  const [validationResult, setValidationResult] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(!!userResponse);
  const [validating, setValidating] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState({});
  const [showCodingTask, setShowCodingTask] = useState(false);
  const [showMCQ, setShowMCQ] = useState(false);
  const [loading, setLoading] = useState({ coding: false, mcq: false });
  const [code, setCode] = useState('');
  const [codeValidationResult, setCodeValidationResult] = useState(null);

  useEffect(() => {
    // Reset state when screen changes
    setGeneratedTasks({});
    setShowCodingTask(false);
    setShowMCQ(false);
    setCodeValidationResult(null);
    
    // Load saved code from localStorage
    if (lessonId && screen.id) {
      const savedCode = localStorage.getItem(`lesson-code-${lessonId}-${screen.id}`);
      if (savedCode) {
        setCode(savedCode);
      }
    }
    
    // Load existing generated tasks for this screen
    if (lessonId && screen.id) {
      handleRequest(() => getGeneratedTasks(lessonId, screen.id))
        .then(response => {
          const tasks = response.data.tasks || [];
          const tasksMap = {};
          tasks.forEach(t => {
            tasksMap[t.type] = t.task;
          });
          setGeneratedTasks(tasksMap);
          if (tasksMap.coding) setShowCodingTask(true);
          if (tasksMap.mcq) setShowMCQ(true);
        })
        .catch(err => console.error('Kunne ikke laste genererte oppgaver:', err));
    }
  }, [lessonId, screen.id, handleRequest]);

  // Auto-save code to localStorage as user types (debounced)
  useEffect(() => {
    if (lessonId && screen.id && code !== undefined) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem(`lesson-code-${lessonId}-${screen.id}`, code);
      }, 500); // Debounce: save 500ms after user stops typing

      return () => clearTimeout(timeoutId);
    }
  }, [code, lessonId, screen.id]);

  const handleGenerateCodingTask = async () => {
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(generateCodingTask, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, coding: response.data.task }));
      setShowCodingTask(true);
    } catch (error) {
      console.error('Kunne ikke generere kodeoppgave:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleGenerateMCQ = async () => {
    setLoading(prev => ({ ...prev, mcq: true }));
    try {
      const response = await handleRequest(generateMCQ, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, mcq: response.data.question }));
      setShowMCQ(true);
    } catch (error) {
      console.error('Kunne ikke generere flervalgsspørsmål:', error);
    } finally {
      setLoading(prev => ({ ...prev, mcq: false }));
    }
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) return;
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(validateGeneratedTask, lessonId, {
        screenId: screen.id,
        taskType: 'coding',
        answer: code
      });
      setCodeValidationResult(response.data);
      if (onAnswer) {
        onAnswer(code, response.data);
      }
    } catch (error) {
      console.error('Kunne ikke validere kode:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleMCQAnswer = async (answer, result) => {
    if (onAnswer) {
      onAnswer(answer, result);
    }
  };

  const handleSubmit = async () => {
    if (!answer.trim()) return;

    setValidating(true);
    const checkingLogic = screen.checkingLogic || {};

    // Try optimistic validation first
    const result = await validateAnswerOptimistic(
      answer,
      checkingLogic,
      {
        useAI: true,
        lessonId,
        screenId: screen.id,
        apiCall: (id, data) => validateInteractiveAnswer(id, data)
      }
    );

    setValidationResult(result);
    setHasAnswered(true);
    setValidating(false);

    if (onAnswer) {
      onAnswer(answer, result);
    }
  };

  return (
    <div className="open-question-screen">
      <div className="open-question-content">
        <div className="question-panel">
          <h3 className="question-text">{screen.contentForTeacherOrSystem}</h3>
          
          <div className="answer-input-area">
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Type your answer here..."
              rows="4"
              className="answer-textarea"
              disabled={hasAnswered}
            />
          </div>

          {validationResult && (
            <div className={`feedback ${validationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
              {validationResult.feedback}
            </div>
          )}

          {!hasAnswered && (
            <button
              className="submit-answer-button"
              onClick={handleSubmit}
              disabled={validating || !answer.trim()}
            >
              {validating ? 'Validerer...' : (screen.uiLayoutHints?.primaryButtonLabel || 'Lever og valider')}
            </button>
          )}

          {/* Generate Task Buttons */}
          {((subjectArea === 'programming' && !showCodingTask) || (!showMCQ && !generatedTasks.mcq)) && (
            <div className="generate-buttons">
              {subjectArea === 'programming' && !showCodingTask && (
                <button
                  className="generate-button generate-coding"
                  onClick={handleGenerateCodingTask}
                  disabled={loading.coding}
                >
                  {loading.coding ? 'Genererer...' : 'Generer kodeoppgave'}
                </button>
              )}
              {!showMCQ && !generatedTasks.mcq && (
                <button
                  className="generate-button generate-mcq"
                  onClick={handleGenerateMCQ}
                  disabled={loading.mcq}
                >
                  {loading.mcq ? 'Genererer...' : 'Generer flervalgsspørsmål'}
                </button>
              )}
            </div>
          )}

          {/* Generated Coding Task */}
          {subjectArea === 'programming' && showCodingTask && generatedTasks.coding && (
            <div className="generated-task coding-task">
              <h3>Kodeoppgave</h3>
              <p><strong>Beskrivelse:</strong> {generatedTasks.coding.description}</p>
              <p><strong>Instruksjoner:</strong> <span dangerouslySetInnerHTML={{ __html: formatInstructions(generatedTasks.coding.instructions) }} /></p>
              {generatedTasks.coding.expectedOutput && (
                <div className="expected-output-generated">
                  <strong>Forventet resultat:</strong>
                  <p dangerouslySetInnerHTML={{ __html: formatExpectedOutput(generatedTasks.coding.expectedOutput) }} />
                </div>
              )}
              <CodeEditor
                value={code}
                onChange={setCode}
                language="javascript"
                height="300px"
              />
              {codeValidationResult && (
                <div className={`feedback ${codeValidationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
                  {codeValidationResult.feedback}
                </div>
              )}
              <button
                className="submit-code-button"
                onClick={handleSubmitCode}
                disabled={loading.coding || !code.trim()}
              >
                {loading.coding ? 'Validerer...' : 'Kjør kode'}
              </button>
            </div>
          )}

          {/* Generated MCQ */}
          {showMCQ && generatedTasks.mcq && (
            <div className="generated-task mcq-task">
              <McqScreen
                screen={generatedTasks.mcq}
                onAnswer={handleMCQAnswer}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpenQuestionScreen;
